import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { useQuery } from 'react-query';
import { clubsAPI } from '../services/api';

const OTHER_ID = '__other__';

const Wrap = styled.div`
  position: relative;
  width: 100%;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 0.875rem;
  box-sizing: border-box;
  background: ${(p) => (p.$readOnly ? '#f1f5f9' : '#fff')};
  opacity: ${(p) => (p.$readOnly ? 0.88 : 1)};

  &:focus {
    outline: none;
    border-color: #043663;
    box-shadow: 0 0 0 3px rgba(4, 54, 99, 0.1);
  }

  &:disabled {
    cursor: not-allowed;
  }
`;

const List = styled.ul`
  position: absolute;
  z-index: 40;
  left: 0;
  right: 0;
  top: calc(100% + 4px);
  margin: 0;
  padding: 0.35rem 0;
  list-style: none;
  max-height: 220px;
  overflow-y: auto;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  box-shadow: 0 10px 25px rgba(15, 23, 42, 0.12);
`;

const Item = styled.li`
  padding: 0.55rem 0.85rem;
  font-size: 0.875rem;
  cursor: pointer;
  background: ${(p) => (p.$active ? '#eff6ff' : 'transparent')};
  color: ${(p) => (p.$muted ? '#043663' : '#1f2937')};
  font-weight: ${(p) => (p.$muted ? 600 : 400)};
  border-top: ${(p) => (p.$divider ? '1px solid #e5e7eb' : 'none')};

  &:hover {
    background: #eff6ff;
  }
`;

const Hint = styled.div`
  margin-top: 0.35rem;
  font-size: 0.75rem;
  color: #6b7280;
`;

const Empty = styled.div`
  padding: 0.75rem 0.85rem;
  font-size: 0.8rem;
  color: #6b7280;
`;

const OtherField = styled.div`
  margin-top: 0.65rem;
`;

const OtherLabel = styled.label`
  display: block;
  font-size: 0.8rem;
  font-weight: 600;
  color: #374151;
  margin-bottom: 0.35rem;
`;

function fold(s) {
  return String(s || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();
}

/**
 * Searchable club dropdown with an "Other" option + free-text field.
 * Value is the club display name (stored on match as court_name/venue).
 */
export default function ClubSearchSelect({
  id,
  value,
  onChange,
  placeholder = 'Search and select a club…',
  disabled = false,
  readOnly = false,
  required = false,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [highlight, setHighlight] = useState(0);
  const [mode, setMode] = useState('catalog'); // 'catalog' | 'other'
  const [otherText, setOtherText] = useState('');
  const wrapRef = useRef(null);
  const locked = disabled || readOnly;

  const { data, isLoading, isError } = useQuery(['clubs-catalog'], () => clubsAPI.list(), {
    staleTime: 60_000,
    enabled: !locked || !!value,
  });

  const clubs = useMemo(() => {
    const rows = data?.data || data || [];
    return Array.isArray(rows) ? rows : [];
  }, [data]);

  // Sync from parent value (edit existing match / load). Do not wipe local search while typing.
  useEffect(() => {
    if (!value) {
      if (!open) {
        setMode('catalog');
        setQuery('');
        setOtherText('');
      }
      return;
    }
    const inList = clubs.some((c) => fold(c.name) === fold(value));
    if (inList) {
      setMode('catalog');
      setQuery(value);
      setOtherText('');
    } else if (mode === 'other' || !clubs.length) {
      setMode('other');
      setQuery('Other');
      setOtherText(value);
    } else {
      // Value not in list yet (clubs still loading) — wait, or treat as Other
      setMode('other');
      setQuery('Other');
      setOtherText(value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, clubs]);

  useEffect(() => {
    const onDoc = (e) => {
      if (!wrapRef.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const filtered = useMemo(() => {
    const q = fold(query);
    if (!q || q === 'other') return clubs;
    return clubs.filter((c) => fold(c.name).includes(q));
  }, [clubs, query]);

  const options = useMemo(
    () => [...filtered, { id: OTHER_ID, name: 'Other' }],
    [filtered]
  );

  const pickClub = (club) => {
    setMode('catalog');
    setQuery(club.name);
    setOtherText('');
    onChange(club.name);
    setOpen(false);
  };

  const pickOther = () => {
    setMode('other');
    setQuery('Other');
    setOpen(false);
    // Keep existing custom text if any; otherwise clear until they type
    if (!otherText.trim()) {
      onChange('');
    } else {
      onChange(otherText.trim());
    }
  };

  const onSearchChange = (e) => {
    if (locked) return;
    const next = e.target.value;
    setMode('catalog');
    setQuery(next);
    setOpen(true);
    setHighlight(0);
    if (fold(next) !== fold(value)) {
      onChange('');
    }
  };

  const onOtherChange = (e) => {
    if (locked) return;
    const next = e.target.value;
    setOtherText(next);
    onChange(next.trim());
  };

  const onKeyDown = (e) => {
    if (locked || !open) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight((h) => Math.min(h + 1, Math.max(options.length - 1, 0)));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const opt = options[highlight];
      if (!opt) return;
      if (opt.id === OTHER_ID) pickOther();
      else pickClub(opt);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  const displayValue = mode === 'other' ? 'Other' : query;
  const committedOk =
    !!String(value || '').trim() &&
    (mode === 'other' || clubs.some((c) => fold(c.name) === fold(value)));

  return (
    <Wrap ref={wrapRef}>
      <Input
        id={id}
        type="text"
        role="combobox"
        aria-expanded={open}
        aria-autocomplete="list"
        aria-controls={id ? `${id}-list` : undefined}
        autoComplete="off"
        placeholder={placeholder}
        value={displayValue}
        disabled={disabled}
        $readOnly={readOnly}
        readOnly={readOnly || mode === 'other'}
        required={required && mode !== 'other'}
        onFocus={() => {
          if (!locked) setOpen(true);
        }}
        onClick={() => {
          if (!locked && mode === 'other') setOpen(true);
        }}
        onChange={onSearchChange}
        onKeyDown={onKeyDown}
      />

      {open && !locked ? (
        <List id={id ? `${id}-list` : undefined} role="listbox">
          {isLoading ? <Empty>Loading clubs…</Empty> : null}
          {isError ? (
            <Empty>Could not load clubs. Ask admin to check Settings → Clubs.</Empty>
          ) : null}
          {!isLoading && !isError && filtered.length === 0 ? (
            <Empty>
              {clubs.length === 0
                ? 'No clubs in the list yet — choose Other, or ask admin to add clubs.'
                : 'No clubs match your search — choose Other below.'}
            </Empty>
          ) : null}
          {filtered.map((c, idx) => (
            <Item
              key={c.id}
              role="option"
              $active={idx === highlight}
              onMouseEnter={() => setHighlight(idx)}
              onMouseDown={(e) => {
                e.preventDefault();
                pickClub(c);
              }}
            >
              {c.name}
              {c.canton ? ` (${c.canton})` : ''}
            </Item>
          ))}
          <Item
            key={OTHER_ID}
            role="option"
            $muted
            $divider
            $active={highlight === filtered.length}
            onMouseEnter={() => setHighlight(filtered.length)}
            onMouseDown={(e) => {
              e.preventDefault();
              pickOther();
            }}
          >
            Other (type a custom name)
          </Item>
        </List>
      ) : null}

      {mode === 'other' && !locked ? (
        <OtherField>
          <OtherLabel htmlFor={id ? `${id}-other` : undefined}>Club name (other) *</OtherLabel>
          <Input
            id={id ? `${id}-other` : undefined}
            type="text"
            value={otherText}
            onChange={onOtherChange}
            placeholder="Type the club or venue name…"
            required={required}
            autoFocus
          />
        </OtherField>
      ) : null}

      {!locked ? (
        <Hint>
          {mode === 'other'
            ? 'Custom name — not on the official list.'
            : committedOk
              ? 'Selected from the official club list.'
              : 'Search and pick a club, or choose Other to type a name.'}
        </Hint>
      ) : null}
    </Wrap>
  );
}
